if (!customElements.get('facet-form')) {
  customElements.define(
    'facet-form',
    class FacetForm extends HTMLFormElement {
      constructor() {
        super();

        this.dirty = false;
        this.cachedMap = new Map();
        this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
        this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');

        this.addEventListener('change', this.onFormChange);
        this.addEventListener('submit', this.onFormSubmit);
      }

      getAnimationParams() {
        return {
          distance: this.isMobile ? 30 : 50,
          duration: this.motionReduced ? 0.001 : (this.isMobile ? 0.3 : 0.5),
          staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.05 : 0.1)
        };
      }

      getVisibleItems(items) {
        const viewportHeight = window.innerHeight + window.scrollY;
        const visible = [];
        const invisible = [];
        
        items.forEach(item => {
          const rect = item.getBoundingClientRect();
          const isVisible = rect.top < viewportHeight && rect.bottom > 0;
          
          if (isVisible) {
            visible.push(item);
          } else {
            invisible.push(item);
          }
        });
        
        return { visible, invisible };
      }

      onFormChange() {
        this.dirty = true;
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      }

      onFormSubmit(event) {
        event.preventDefault();
        if (!this.dirty) return;

        const url = this.buildUrl().toString();
        this.renderSection(url, event);
      }

      buildUrl() {
        const searchParams = new URLSearchParams(new FormData(this));
        const url = new URL(this.action);

        url.search = '';
        searchParams.forEach((value, key) => url.searchParams.append(key, value));

        ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
          if (url.searchParams.get(item) === '') {
            url.searchParams.delete(item);
          }
        });

        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      updateURLHash(url) {
        const clonedUrl = new URL(url);
        clonedUrl.searchParams.delete('section_id');
        history.replaceState({}, '', clonedUrl.toString());
      }

      beforeRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        const items = container.querySelectorAll('.product-card, .pgf-collection-product');

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(items);

        Motion.timeline([
          [visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: duration, delay: Motion.stagger(staggerDelay) }],
          [inVisibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: duration }],
          [container, { y: distance, opacity: 0 }, { duration: duration, easing: 'linear' }]
        ]);
        items.forEach(item => item.style.removeProperty('transform'));

        let headerHeight = 0;
        if (!theme.config.mqlSmall && document.querySelector('header.header')) {
          headerHeight = Math.round(document.querySelector('header.header').clientHeight);
        }
        setTimeout(() => {
          const target = document.querySelector('.collection');
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20) - headerHeight,
            behavior: theme.config.motionReduced ? 'auto' : 'smooth'
          });

          const drawer = document.getElementById('FacetDrawer');
          if (drawer) drawer.classList.add('loading');
          const drawerSticky = document.getElementById('FacetDrawerSticky');
          if (drawerSticky) drawerSticky.classList.add('loading');
        }, 100);
      }

      afterRenderSection() {
        const container = document.getElementById('ProductGridContainer');
        const items = container.querySelectorAll('.product-card, .pgf-collection-product');

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(items);

        Motion.timeline([
          [container, { y: [distance, 0], opacity: [0, 1] }, { duration: duration, easing: 'linear' }],
          [visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, delay: Motion.stagger(staggerDelay) }],
          [inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration }]
        ]);
        items.forEach(item => item.style.removeProperty('transform'));

        const drawer = document.getElementById('FacetDrawer');
        if (drawer) drawer.classList.remove('loading');
        const drawerSticky = document.getElementById('FacetDrawerSticky');
        if (drawerSticky) drawerSticky.classList.remove('loading');

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      }

      renderSection(url, event) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, event)
          : this.renderSectionFromFetch(url, event);

        if (this.hasAttribute('data-history')) this.updateURLHash(url);

        this.dirty = false;
      }

      renderSectionFromFetch(url, event) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.beforeRenderSection();
        const start = performance.now();

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const execution = (performance.now() - start);

            setTimeout(() => {
              this.renderFilters(responseText, event);
              this.renderFiltersActive(responseText);
              this.renderProductGridContainer(responseText);
              this.renderProductCount(responseText);
              this.renderSortBy(responseText);
              this.syncFacetFormActions(responseText);
              this.renderCollectionSeriesNav(responseText);
              this.renderCatalogHeaderFromResponse(responseText);

              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
              this.cachedMap.set(url, responseText);

              this.afterRenderSection();
            }, execution > 500 ? 0 : 500);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSectionFromCache(url, event) {
        this.beforeRenderSection();

        setTimeout(() => {
          const responseText = this.cachedMap.get(url);
          this.renderFilters(responseText, event);
          this.renderFiltersActive(responseText);
          this.renderProductGridContainer(responseText);
          this.renderProductCount(responseText);
          this.renderSortBy(responseText);
          this.syncFacetFormActions(responseText);
          this.renderCollectionSeriesNav(responseText);
          this.renderCatalogHeaderFromResponse(responseText);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

          this.afterRenderSection();
        }, 500);
      }

      syncFacetFormActions(responseText) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const oldForms = [...document.querySelectorAll('form[is="facet-form"]')];
        const newForms = [...parsedHTML.querySelectorAll('form[is="facet-form"]')];
        const n = Math.min(oldForms.length, newForms.length);
        for (let i = 0; i < n; i++) {
          const nextAction = newForms[i].getAttribute('action');
          if (nextAction) oldForms[i].setAttribute('action', nextAction);
        }
      }

      renderCollectionSeriesNav(responseText) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const pairs = [
          ['#FacetFiltersContainer .collection__collections-nav', '#FacetFiltersContainer .collection__collections-nav'],
          ['#MobileFacetFiltersContainer .drawer__collections-block', '#MobileFacetFiltersContainer .drawer__collections-block'],
        ];
        for (const [selFrom, selTo] of pairs) {
          const next = parsedHTML.querySelector(selFrom);
          const cur = document.querySelector(selTo);
          if (next && cur) cur.replaceWith(next.cloneNode(true));
        }
      }

      renderCatalogHeaderFromResponse(responseText) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const h1Old = document.querySelector('.collection__catalog-header > h1.heading');
        const h1New = parsedHTML.querySelector('.collection__catalog-header > h1.heading');
        if (h1Old && h1New) h1Old.textContent = h1New.textContent;

        const navOld = document.querySelector('.collection__catalog-breadcrumb nav.breadcrumb');
        const navNew = parsedHTML.querySelector('.collection__catalog-breadcrumb nav.breadcrumb');
        if (navOld && navNew) navOld.innerHTML = navNew.innerHTML;

        const countOld = document.querySelector('.collection__catalog-toolbar-mobile-count');
        const countNew = parsedHTML.querySelector('.collection__catalog-toolbar-mobile-count');
        if (countOld && countNew) countOld.innerHTML = countNew.innerHTML;

        const oldCover = document.getElementById('CollectionCatalogCover');
        const newCover = parsedHTML.getElementById('CollectionCatalogCover');
        if (!oldCover && !newCover) return;

        if (newCover) {
          const next = newCover.cloneNode(true);
          if (oldCover) {
            oldCover.replaceWith(next);
          } else {
            const collectionRoot = document.querySelector('.collection.collection--catalog');
            if (collectionRoot) {
              collectionRoot.insertBefore(next, collectionRoot.firstElementChild);
            }
          }
        } else if (oldCover) {
          oldCover.remove();
        }
      }

      renderFilters(responseText, event) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const facetElements = parsedHTML.querySelectorAll(
          '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
        );

        const matchesIndex = (element) => {
          const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
          return jsFilter ? element.getAttribute('data-index') === jsFilter.getAttribute('data-index') : false;
        };
        const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

        facetsToRender.forEach((element) => {
          const filter = document.querySelector(`[data-filter][data-index="${element.getAttribute('data-index')}"]`);
          if (filter !== null) {
            if (filter.tagName === 'DETAILS') {
              filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
            }
            else {
              filter.innerHTML = element.innerHTML;
            }
          }
        });
      }

      renderFiltersActive(responseText) {
        this._updateSection(responseText, 'FacetFiltersActive');
      }

      renderProductGridContainer(responseText) {
        this._updateSection(responseText, 'ProductGridContainer', (newContent) => {
          newContent.querySelector('motion-list')?.setAttribute('motion-reduced', '');
        });
      }

      renderProductCount(responseText) {
        this._updateSection(responseText, 'ProductCount');
      }

      renderSortBy(responseText) {
        this._updateSection(responseText, 'SortByContainer', (newContent, oldContainer) => {
          const oldFacetSort = oldContainer.querySelector('facet-sort');
          const newFacetSort = newContent.querySelector('facet-sort');
          if (!oldFacetSort || !newFacetSort) return;
          
          if (oldFacetSort.hasAttribute('style')) {
            newFacetSort.setAttribute('style', oldFacetSort.getAttribute('style'));
          }
        });
      }

      _updateSection(responseText, sectionId, modifier = null) {
        const container = document.getElementById(sectionId);
        if (!container) return false;

        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newContent = parsedHTML.getElementById(sectionId);
        if (!newContent) return false;

        // Apply custom modifications if provided
        modifier?.(newContent, container);

        container.innerHTML = newContent.innerHTML;
        return true;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('facet-count')) {
  customElements.define(
    'facet-count',
    class FacetCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      get itemCount() {
        return parseInt(this.innerText);
      }

      onFacetUpdate(event) {
        const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
        const facetCount = parsedHTML.querySelector('facet-count');
        this.innerText = facetCount.innerHTML;
        this.hidden = this.itemCount === 0;
      }
    }
  );
}

if (!customElements.get('results-count')) {
  customElements.define(
    'results-count',
    class ResultsCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      onFacetUpdate(event) {
        const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
        const resultsCount = parsedHTML.querySelector('results-count');
        this.innerText = resultsCount.innerHTML;
      }
    }
  );
}

if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    class FacetRemove extends MagnetLink {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          event.preventDefault();

          const url = new URL(this.href);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          form.renderSection(url.toString(), event);
        }
      }
    }, { extends: 'a' }
  );
}

/** Sort diagnostics: `?facet_sort_debug=1`, `sessionStorage facet-sort-debug`, or `window.FACET_SORT_DEBUG`. Pointer probe: snippet `facet-sort-pointer-probe` + URL contains `facet_sort`. */
const FacetSortDebug = {
  enabled() {
    try {
      if (typeof window === 'undefined') return false;
      if (window.FACET_SORT_DEBUG === true) return true;
      if (window.sessionStorage?.getItem('facet-sort-debug') === '1') return true;
      const q = new URLSearchParams(window.location.search);
      if (q.get('facet_sort_debug') === '1') return true;
      if (q.get('facet_sort_probe') === '1') return true;
      if ((window.location.search || '').indexOf('facet_sort') !== -1) return true;
      return false;
    }
    catch (_) {
      return false;
    }
  },
  log(...args) {
    if (this.enabled()) console.log('[facet-sort]', ...args);
  }
};

if (!customElements.get('facet-sort')) {
  try {
    customElements.define(
      'facet-sort',
      class FacetSort extends HTMLElement {
      constructor() {
        super();

        this._onChange = this.onChange.bind(this);
        this._onFacetClick = this.onFacetClick.bind(this);
        this._onWindowClick = this.onWindowClick.bind(this);
        this._onDebugPointer = this.onDebugPointer.bind(this);
        this._onDebugDocCapture = this.onDebugDocCapture.bind(this);

        FacetSortDebug.log('constructor ok', { motion: typeof Motion, theme: typeof theme });
      }

      connectedCallback() {
        FacetSortDebug.log('connectedCallback', {
          hasButton: !!this.button,
          hasSelection: !!this.selection,
          hasListbox: !!this.listbox
        });

        this.addEventListener('change', this._onChange);
        this.addEventListener('click', this._onFacetClick);
        document.addEventListener('click', this._onWindowClick);

        if (FacetSortDebug.enabled()) {
          this.addEventListener('pointerdown', this._onDebugPointer, true);
          document.addEventListener('pointerdown', this._onDebugDocCapture, true);
        }

        const bindInView = () => {
          if (typeof Motion !== 'undefined' && Motion.inView) {
            Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
          }
        };

        requestAnimationFrame(() => {
          try {
            this.init();
            bindInView();
            FacetSortDebug.log('init rAF done', { motion: typeof Motion });
          }
          catch (err) {
            console.error('[facet-sort] init rAF error', err);
          }
        });
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onChange);
        this.removeEventListener('click', this._onFacetClick);
        document.removeEventListener('click', this._onWindowClick);
        this.removeEventListener('pointerdown', this._onDebugPointer, true);
        document.removeEventListener('pointerdown', this._onDebugDocCapture, true);
      }

      onDebugPointer(event) {
        FacetSortDebug.log('pointerdown (capture on facet-sort)', event.target?.nodeName, event.target?.className);
      }

      onDebugDocCapture(event) {
        if (!this.contains(event.target)) return;
        FacetSortDebug.log('pointerdown (capture on document, target inside this facet-sort)', event.target?.nodeName, event.target?.className);
      }

      get listbox() {
        return this.querySelector('.sort-listbox');
      }

      get selection() {
        return this.querySelector('.sort-selection');
      }

      get button() {
        return this.querySelector('.sort-by');
      }

      get close() {
        return this.querySelector('.sort-close');
      }

      init() {
        if (!this.selection || !this.listbox) return;
        this.initButton();
        this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height.toFixed(1)}px`);
      }

      initButton() {
        if (!this.selection) return;
        const value = this.selection.innerText;
        const width = theme.getElementWidth(this.selection, value);
        this.style.setProperty('--facet-button-width', `${width}px`);
      }

      onFacetClick(event) {
        FacetSortDebug.log('click (bubble on facet-sort)', {
          target: event.target?.nodeName,
          className: event.target?.className,
          open: this.button?.hasAttribute('open')
        });

        const button = this.button;
        if (!button) {
          FacetSortDebug.log('onFacetClick abort: no .sort-by');
          return;
        }

        if (event.target.closest('.sort-listbox')) {
          FacetSortDebug.log('onFacetClick: ignored (inside .sort-listbox)');
          return;
        }

        if (event.target.closest('.sort-close')) {
          if (button.hasAttribute('open')) {
            FacetSortDebug.log('onFacetClick: chevron → hide');
            event.preventDefault();
            this.hide();
          }
          else {
            FacetSortDebug.log('onFacetClick: chevron → show');
            this.show();
          }
          return;
        }

        if (button.contains(event.target)) {
          FacetSortDebug.log('onFacetClick: inside button → show');
          this.show();
        }
        else {
          FacetSortDebug.log('onFacetClick: no-op (target not inside .sort-by)');
        }
      }

      onChange(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', event.target.value);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          url.searchParams.delete('page');
          form.renderSection(url.toString(), event);
        }

        if (this.selection && event.target.nextElementSibling) {
          this.selection.innerText = event.target.nextElementSibling.innerText;
        }
        this.initButton();
        this.hide();
      }

      show() {
        const button = this.button;
        if (!button) {
          FacetSortDebug.log('show abort: no button');
          return;
        }
        button.setAttribute('open', '');
        FacetSortDebug.log('show → set open on .sort-by', { open: button.hasAttribute('open') });
      }

      hide(immediate = true) {
        const button = this.button;
        if (!button || !button.hasAttribute('open')) {
          FacetSortDebug.log('hide skip (no button or not open)');
          return;
        }

        FacetSortDebug.log('hide → remove open after delay', { immediate });
        setTimeout(() => {
          button.removeAttribute('open');
        }, 100);

        if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;

        const btnFill = button.querySelector('[data-fill]');
        if (btnFill) {
          Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
        }
      }

      onWindowClick(event) {
        const button = this.button;
        if (!button) return;
        if (!this.contains(event.target) && button.hasAttribute('open')) {
          FacetSortDebug.log('document click outside facet-sort → hide');
          this.hide(false);
        }
      }
    }
    );
  }
  catch (err) {
    console.error('[facet-sort] customElements.define failed', err);
  }
}

queueMicrotask(() => {
  if (!FacetSortDebug.enabled()) return;
  console.log('[facet-sort] probe after parse', {
    defined: !!customElements.get('facet-sort'),
    domCount: document.querySelectorAll('facet-sort').length
  });
});

if (!customElements.get('facet-sticky')) {
  customElements.define(
    'facet-sticky',
    class FacetSticky extends HTMLElement {
      constructor() {
        super();
      }

      get button() {
        return this.querySelector('.facet-sticky__float-btn') || this.querySelector('.button');
      }

      connectedCallback() {
        const btn = this.button;
        if (!btn) return;

        /* Previously: IntersectionObserver on .collection-section with aggressive rootMargin.
           - observe(null) threw when .collection-section was missing → custom element never worked.
           - rootMargin became invalid (e.g. --100px) when availHeight < 200.
           - First callback false hid the button before users could open the drawer.
           Mobile filter entry should stay visible while this host is shown (xl:hidden). */
        btn.style.opacity = '1';
        btn.style.visibility = 'visible';
        btn.style.transform = 'translateY(0)';
      }
    }
  );
}

if (!customElements.get('price-range')) {
  customElements.define(
    'price-range',
    class PriceRange extends HTMLElement {
      constructor() {
        super();

        this.rangeMin = this.querySelector('input[type="range"]:first-child');
        this.rangeMax = this.querySelector('input[type="range"]:last-child');
        this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
        this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

        this.inputMin.addEventListener('focus', this.inputMin.select);
        this.inputMax.addEventListener('focus', this.inputMax.select);
        this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
        this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

        this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
        this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
        this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
        this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
      }

      onInputMinChange(event) {
        event.preventDefault();
        event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1), event.target.min);
        this.rangeMin.value = event.target.value;
        this.rangeMin.parentElement.style.setProperty('--range-min', `${parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max) * 100}%`);
      }

      onInputMaxChange(event) {
        event.preventDefault();
        event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1), event.target.max);
        this.rangeMax.value = event.target.value;
        this.rangeMax.parentElement.style.setProperty('--range-max', `${parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max) * 100}%`);
      }

      onRangeMinChange(event) {
        event.stopPropagation();
        this.inputMin.value = event.target.value;
        this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMaxChange(event) {
        event.stopPropagation();
        this.inputMax.value = event.target.value;
        this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMinInput(event) {
        event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty('--range-min', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMin.value = event.target.value;
      }

      onRangeMaxInput(event) {
        event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty('--range-max', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMax.value = event.target.value;
      }
    }
  );
}

if (!customElements.get('infinite-button')) {
  customElements.define(
    'infinite-button',
    class InfiniteButton extends HoverButton {
      constructor() {
        super();

        this.onClickHandler = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);

        if (this.getAttribute('mode') == 'infinite') {
          Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
      }

      onClick() {
        if (this.hasAttribute('aria-busy')) return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        this.enableLoading();
        const url = this.buildUrl().toString();

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderPagination(responseText);
            this.renderProductGridContainer(responseText);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderPagination(responseText) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = productGridContainer.querySelector('.pagination');
        const source = parsedHTML.querySelector('.pagination');

        if (source) {
          destination.innerHTML = source.innerHTML;
        }
        else {
          destination.remove();
        }
      }

      renderProductGridContainer(responseText) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = productGridContainer.querySelector('motion-list');
        const source = parsedHTML.querySelector('motion-list');

        if (source && destination) {
          source.querySelectorAll('.card').forEach((item) => {
            destination.appendChild(item);
          });

          destination.reload();
        }
      }
      
      buildUrl() {
        const url = new URL(this.getAttribute('action'));
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      enableLoading() {
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-busy', 'true');
      }
    }, { extends: 'button' }
  );
}

if (!customElements.get('facets-topbar')) {
  customElements.define(
    'facets-topbar',
    class FacetsTopbar extends StickyElement {
      constructor() {
        super();

        this._onResize = theme.utils.throttle(() => this.positionStickySidebar());
      }

      connectedCallback() {
        requestAnimationFrame(() => this.positionStickySidebar());
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
        if (typeof this._onResize.cancel === 'function') this._onResize.cancel();
        document.body.classList.remove('facets-topbar--active');
      }

      positionStickySidebar() {
        const computed = window.getComputedStyle(this);
        if (computed.display === 'none' || computed.visibility === 'hidden') {
          document.body.classList.remove('facets-topbar--active');
          return;
        }

        const bounding = this.getBoundingClientRect();
        if (bounding.height < 1 || bounding.width < 1) {
          document.body.classList.remove('facets-topbar--active');
          return;
        }

        const stuckToViewportTop = Math.abs(bounding.top) < 1;
        if (stuckToViewportTop) {
          document.body.classList.add('facets-topbar--active');
        }
        else {
          document.body.classList.remove('facets-topbar--active');
        }
      }
    }
  );
}

if (!customElements.get('model-view')) {
  customElements.define(
    'model-view',
    class ModelView extends MagnetButton {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this._open = this.hasAttribute('open');
        this.onClickHandler = this.onClick.bind(this);
        this.textSpans = this.querySelectorAll('.link-text');
      }

      set open(value) {
        if (value !== this._open) {
          this._open = value;
          value ? this.setAttribute('open', '') : this.removeAttribute('open');
        }
      }

      static get observedAttributes() {
        return ['open'];
      }

      get open() {
        return this._open;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'open') {
          const isActive = newValue === '';
          this.setActiveModel(isActive);
          this.updateAccessibility(isActive);
        }
      }

      init() {
        if (theme.config.hasLocalStorage) {
          const isActive = window.localStorage.getItem(`${theme.settings.themeName}:model-view`) || 'false';
          this.open = isActive === 'true';
        }
      }

      onClick(event) {
        event.preventDefault();
        this.open = !this.open;
      }

      setActiveModel(isActive) {
        const container = document.getElementById('ProductGridContainer');
        container.classList.toggle('lifestyle', isActive);

        container.querySelectorAll('video-media').forEach((videoMedia) => videoMedia.pause());

        if (theme.config.hasLocalStorage) {
          window.localStorage.setItem(`${theme.settings.themeName}:model-view`, isActive);
        }
      }

      updateAccessibility(isActive) {
        if (this.textSpans.length === 2) {
          const [defaultText, activeText] = this.textSpans;
          this.setAttribute('aria-label', isActive ? activeText.textContent.trim() : defaultText.textContent.trim());
        }
      }
    }, { extends: 'button' }
  );
}

if (!customElements.get('sub-collections')) {
  customElements.define(
    'sub-collections',
    class SubCollections extends HTMLSelectElement {
      constructor() {
        super();
        this.addEventListener('change', this.onChange);
      }
    
      onChange() {
        window.location.href = this.value;
      }
    }, { extends: 'select' }
  );
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a.subcollections--radio-list__link');
  if (!link?.href) return;
  if (!document.querySelector('.collection--catalog')) return;
  if (link.classList.contains('is-active')) {
    event.preventDefault();
    return;
  }
  if (event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.button !== 0) return;

  const form = document.querySelector('form[is="facet-form"]');
  if (!form || typeof form.renderSection !== 'function') return;

  try {
    const dest = new URL(link.href);
    if (dest.origin !== window.location.origin) return;
    if (!dest.pathname.includes('/collections/')) return;

    event.preventDefault();
    dest.searchParams.set('section_id', theme.utils.sectionId(form));
    form.renderSection(dest.toString(), event);
  }
  catch (err) {
    console.error(err);
  }
});

